/**
 * In-page anchors for the shared article reader (ArticleView — tutorials and
 * converted scenes). CAS migration plan O2 / D20, §2.3 item 3.
 *
 * Every TOC entry gets ONE primary anchor — its title slug, de-duplicated in
 * TOC order (`step-by-step`, `step-by-step-2`, …) exactly as before — which
 * becomes the section's DOM `id` and the `#hash` the TOC writes. A chapter
 * may also carry explicit anchors (its editable `anchorId` plus legacy
 * `anchorIds`: restored WordPress anchors, former CAS media-tab keys). Those
 * render as extra empty anchor targets inside the section.
 *
 * Rules (no duplicate DOM ids):
 *   • Explicit anchors are registered first; the first section to claim a
 *     value owns it.
 *   • A derived title slug skips any value already taken — by an earlier
 *     derived anchor or by ANOTHER section's explicit anchor — moving on to
 *     `-2`, `-3`, … A section may take a value it owns itself.
 *   • An explicit anchor equal to the section's own primary anchor is not
 *     emitted again as an extra; repeated values are emitted once.
 *
 * Plain `.ts` so the client view and unit tests share it (no 'use client').
 */

export interface AnchorEntry {
  /** Opaque section id (scroll-spy / ref key). */
  id: string
  /** Slugified label (already non-empty; caller substitutes 'section'). */
  base: string
  /** Explicit anchors for this section (chapters only), already validated. */
  explicit?: readonly string[] | null
}

export interface ResolvedAnchors {
  /** section id → primary anchor (DOM id + TOC hash). */
  anchorById: Record<string, string>
  /** primary anchor → section id (hash lookup). */
  idByAnchor: Record<string, string>
  /** section id → extra anchor ids to render inside the section. */
  extraAnchorsById: Record<string, string[]>
}

export function resolveSectionAnchors(entries: readonly AnchorEntry[]): ResolvedAnchors {
  // 1. Explicit anchors first — first claim wins.
  const owner = new Map<string, string>()
  for (const e of entries) {
    for (const a of e.explicit ?? []) {
      if (typeof a === 'string' && a && !owner.has(a)) owner.set(a, e.id)
    }
  }

  // 2. Derived primary anchors, de-duplicated in TOC order. The per-base
  //    counter keeps the historical `base`, `base-2`, `base-3` numbering.
  const anchorById: Record<string, string> = {}
  const idByAnchor: Record<string, string> = {}
  const used = new Set<string>()
  const counts = new Map<string, number>()
  for (const e of entries) {
    let n = counts.get(e.base) ?? 0
    let candidate: string
    for (;;) {
      candidate = n === 0 ? e.base : `${e.base}-${n + 1}`
      n++
      const o = owner.get(candidate)
      if (used.has(candidate) || (o !== undefined && o !== e.id)) continue
      break
    }
    counts.set(e.base, n)
    anchorById[e.id] = candidate
    idByAnchor[candidate] = e.id
    used.add(candidate)
  }

  // 3. Extras: the section's own explicit anchors minus its primary, minus
  //    values another section already owns / uses, each once.
  const extraAnchorsById: Record<string, string[]> = {}
  for (const e of entries) {
    if (!e.explicit || e.explicit.length === 0) continue
    const out: string[] = []
    for (const a of e.explicit) {
      if (typeof a !== 'string' || !a) continue
      if (a === anchorById[e.id]) continue
      if (owner.get(a) !== e.id || used.has(a) || out.includes(a)) continue
      out.push(a)
    }
    extraAnchorsById[e.id] = out
  }

  return { anchorById, idByAnchor, extraAnchorsById }
}
